<template>
  <div class="w-full h-full flex flex-col bg-white overflow-hidden">
    <div class="h-full w-full flex flex-col py-8 gap-4">
      <!-- Image container with fixed height -->
      <div class="h-[60%] w-full flex-shrink-0 relative overflow-hidden">
        <transition name="slide-rtl">
          <div :key="currentStep" class="absolute inset-0 w-full h-full flex justify-center items-center">
            <img
                v-if="currentStep !== 0"
                :src="selectedStep.image"
                class="max-h-full max-w-full object-contain transition-transform duration-500"
            />
            <div v-else class="w-full h-full flex justify-center items-center">
              <interactive width="250px" height="250px"/>
            </div>
          </div>
        </transition>
      </div>

      <!-- Text container with fixed height -->
      <div class="h-48 relative w-full flex-shrink-0 overflow-hidden">
        <transition name="slide-rtl">
          <div :key="currentStep" class="absolute inset-0 w-full h-full flex flex-col gap-4">
            <span class="blue-text px-8 text-4xl text-center font-semibold">{{ selectedStep.title }}</span>
            <span class="font-light px-8 text-pretty blue-text">{{ selectedStep.description }}</span>
          </div>
        </transition>
      </div>
    </div>

    <div class="flex justify-center items-center px-4 py-6 mt-auto w-full flex-col gap-4">
      <div class="flex justify-center gap-2 pb-4">
        <div
            v-for="(step, index) of steps"
            :key="step.title"
            class="w-2 h-2 rounded-full bg-gray-400 transition-colors duration-300"
            :class="[index === currentStep ? 'bg-gray-700' : '']"
        />
      </div>
      <Button
          v-wave
          :label="currentStep === steps.length - 1 ? 'סיום' : 'הבא'"
          :loading="loading"
          class="w-full"
          :pt="{root: {class: '!p-4 !rounded-xl'}}"
          @click="onClickNext"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import Interactive from "../auth/Interactive.vue";

const emit = defineEmits(['finished']);

const steps: Array<{
  image: string;
  title: string;
  description: string;
}> = [
  {
    image: new URL(`../../../assets/img/login/walkthrough-cover.png`, import.meta.url).href,
    title: 'אפליקציית ניהול לעסק הביוטי שלך',
    description: ' לנהל עסק ביוטי זה מורכב! את צריכה לשמר ולמשוך לקוחות, לגבות תשלומים, להפיק חשבוניות, לנהל יומן ועוד. וכמובן, לספק שירות מעולה ללקוחות שלך. בשביל זה יש timeit - השירות המעולה ללקוחות עליך והשאר עלינו.'
  },
  {
    image: new URL(`../../../assets/img/login/appointment-scheduling.png`, import.meta.url).href,
    title: 'זימון תורים',
    description: 'הזמנת תורים עצמאית על ידי לקוחות 24/7, תזכורות לפני התור ובקשות לאישור הגעה, עדכון התור באופן עצמאי, ורישום לרשימת המתנה עם עדכונים בזמן אמת על תורים שהתפנו - הכל לצורך חוויית לקוח משופרת ותהליך יעיל.'
  },
  {
    image: new URL(`../../../assets/img/login/crm.png`, import.meta.url).href,
    description: 'ניהול המידע שלכם על הלקוחות - פרטים אישיים, היסטוריית התקשרות והעדפות בצורה פשוטה וקלה. שמירה על קשר עם הלקוחות באמצעות הודעות בתפוצה רחבה, אפשרות לתשלום מקוון נוח ומהיר עבור הלקוחות, וניהול חשבוניות אוטומטי.',
    title: 'ניהול לקוחות'
  },
  {
    image: new URL(`../../../assets/img/login/converation-tools.png`, import.meta.url).href,
    description: 'דף נחיתה מותאם אישית עם עם כל הפרטים שלכם. שליחת הודעות אוטומטיות ללקוחות, מועדון לקוחות לחיזוק הקשר עם הלקוחות באמצעות הטבות, צבירת נקודות או טיפולים, חנות פיקאפ משלכם לאיסוף מוצרים בתור ועוד. וכמובן, דוחות וניתוחים לתמונת מצב עדכנית של העסק.',
    title: 'כלי מכירה'
  },
];

const loading = ref(false);
const currentStep = ref(0);

const selectedStep = computed(() => steps[currentStep.value]);

const onClickNext = () => {
  if (currentStep.value === steps.length - 1) {
    emit('finished');
  } else {
    currentStep.value = currentStep.value + 1;
  }
};
</script>

<style scoped lang="scss">
.blue-text {
  color: #2a0083;
  border: none;
}

// RTL Slide Animation - Consistent for both image and text
.slide-rtl-enter-active,
.slide-rtl-leave-active {
  transition: all 0.5s ease-in-out;
  position: absolute;
  width: 100%;
  height: 100%;
}

.slide-rtl-enter-from {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-rtl-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.slide-rtl-enter-to,
.slide-rtl-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
